import { useCallback, useEffect, useMemo, useState } from "react";
import { RotateCcw, Search, Trash2 } from "lucide-react";
import Swal from "sweetalert2";
import { formatDate } from "../../core/api";
import { deleteReview as deleteReviewRequest, getReviews } from "../../core/services/reviewService";
import MetricCard from "../../shared/MetricCard";
import PaginationBar, { slicePage } from "../../shared/PaginationBar";

type ReviewFilters = {
  search: string;
  rating: string;
  product: string;
};

const Reviews = () => {
  const [reviews, setReviews] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(10);
  const [filters, setFilters] = useState<ReviewFilters>({
    search: "",
    rating: "all",
    product: "",
  });

  const loadReviews = useCallback(async () => {
    setLoading(true);

    try {
      const params = new URLSearchParams();

      if (filters.search.trim()) params.set("search", filters.search.trim());
      if (filters.rating !== "all") params.set("rating", filters.rating);
      if (filters.product.trim()) params.set("product", filters.product.trim());

      const endpoint = params.toString() ? `/reviews?${params}` : "/reviews";
      const result = await getReviews(endpoint);
      setReviews(result.reviews || []);
    } catch (error: any) {
      Swal.fire("Error", error.message || "Unable to load reviews", "error");
    } finally {
      setLoading(false);
    }
  }, [filters]);

  useEffect(() => {
    loadReviews();
  }, [loadReviews]);

  useEffect(() => {
    setPage(1);
  }, [filters]);

  const metrics = useMemo(() => {
    const total = reviews.length;
    const average = total
      ? reviews.reduce((sum, review) => sum + Number(review.rating || 0), 0) / total
      : 0;

    return {
      total,
      average: average.toFixed(1),
      fiveStar: reviews.filter((review) => Number(review.rating) === 5).length,
    };
  }, [reviews]);

  const paged = slicePage(reviews, page, limit);

  const deleteReview = async (review: any) => {
    const confirmation = await Swal.fire({
      title: "Delete review?",
      text: "This review will be removed permanently and product ratings will update.",
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#ef4444",
      confirmButtonText: "Delete",
    });

    if (!confirmation.isConfirmed) return;

    try {
      const result = await deleteReviewRequest(review._id);

      if (!result.success) {
        throw new Error(result.message || "Review delete failed");
      }

      await Swal.fire({
        title: "Review Deleted",
        icon: "success",
        timer: 1200,
        showConfirmButton: false,
      });
      loadReviews();
    } catch (error: any) {
      Swal.fire("Delete Failed", error.message || "Unable to delete review", "error");
    }
  };

  const resetFilters = () => {
    setFilters({ search: "", rating: "all", product: "" });
  };

  return (
    <>
      <div className="page-hdr">
        <div className="page-hdr-left">
          <div className="page-eyebrow">CUSTOMER FEEDBACK</div>
          <h1 className="page-title">
            Product <em>Reviews</em>
          </h1>
          <div className="page-sub">Review customer feedback and keep product ratings accurate.</div>
        </div>
      </div>

      <section className="metrics-grid">
        <MetricCard metric={{ label: "Total Reviews", value: metrics.total, icon: "💬", tone: "blue" }} />
        <MetricCard metric={{ label: "Average Rating", value: metrics.average, icon: "⭐", tone: "orange" }} />
        <MetricCard metric={{ label: "Five Star", value: metrics.fiveStar, icon: "🌟", tone: "purple" }} />
      </section>

      <div className="toolbar">
        <div className="toolbar-search">
          <Search size={18} aria-hidden="true" />
          <input
            value={filters.search}
            onChange={(event) => setFilters({ ...filters, search: event.target.value })}
            placeholder="Search reviewer name or comment..."
          />
        </div>

        <select
          className="filter-select"
          value={filters.rating}
          onChange={(event) => setFilters({ ...filters, rating: event.target.value })}
        >
          <option value="all">All Ratings</option>
          <option value="5">5 Stars</option>
          <option value="4">4 Stars</option>
          <option value="3">3 Stars</option>
          <option value="2">2 Stars</option>
          <option value="1">1 Star</option>
        </select>

        <input
          className="filter-select"
          value={filters.product}
          onChange={(event) => setFilters({ ...filters, product: event.target.value })}
          placeholder="Product ID"
        />

        <div className="toolbar-right">
          <button type="button" className="btn-sm btn-outline" onClick={resetFilters}>
            <RotateCcw size={16} aria-hidden="true" />
            Reset Filters
          </button>
        </div>
      </div>

      <section className="table-card">
        <div className="table-head">
          <h2>Customer Reviews ({reviews.length})</h2>
          <p>All submitted product reviews.</p>
        </div>

        <div className="table-scroll">
          <table>
            <thead>
              <tr>
                <th>Customer</th>
                <th>Product</th>
                <th>Rating</th>
                <th>Comment</th>
                <th>Date</th>
                <th>Action</th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr>
                  <td colSpan={6} className="empty-state">Loading reviews...</td>
                </tr>
              ) : reviews.length === 0 ? (
                <tr>
                  <td colSpan={6} className="empty-state">No reviews match these filters.</td>
                </tr>
              ) : (
                paged.rows.map((review) => (
                  <tr key={review._id}>
                    <td>
                      <strong>{review.user?.name || "Deleted user"}</strong>
                      <small className="table-muted">{review.user?.email || "-"}</small>
                    </td>
                    <td>{review.product?.name || "Deleted product"}</td>
                    <td>
                      <span className="status-badge warning">
                        <span aria-hidden="true">⭐</span> {review.rating}/5
                      </span>
                    </td>
                    <td>{review.comment || "-"}</td>
                    <td>{formatDate(review.createdAt)}</td>
                    <td>
                      <button
                        type="button"
                        className="action-button delete"
                        title="Delete review"
                        aria-label="Delete review"
                        onClick={() => deleteReview(review)}
                      >
                        <Trash2 size={16} aria-hidden="true" />
                      </button>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>

        <PaginationBar
          page={paged.page}
          limit={limit}
          total={paged.total}
          totalPages={paged.totalPages}
          itemLabel="reviews"
          onPageChange={setPage}
          onLimitChange={(nextLimit) => {
            setPage(1);
            setLimit(nextLimit);
          }}
        />
      </section>
    </>
  );
};

export default Reviews;
